import { useEffect, useState } from "react";
import { Megaphone, BookOpenText, Send, Plus, CheckCircle2, XCircle, Users, ChevronDown, ChevronUp, Archive, AlertTriangle } from "lucide-react";
import ProgressLoader from "@/components/progress-loader";

const BASE = import.meta.env.BASE_URL;

function adminHeaders(json = false): HeadersInit {
  const token = localStorage.getItem("admin_token") || "";
  const h: Record<string, string> = { "x-admin-token": token };
  if (json) h["Content-Type"] = "application/json";
  return h;
}

type Announcement = {
  id: number;
  cohortId: number;
  subject: string;
  body: string;
  status: "draft" | "sent" | "partial";
  createdBy: string | null;
  sentAt: string | null;
  recipientCount: number | null;
  deliveredCount: number;
  failedCount: number;
  createdAt: string;
};

type Delivery = {
  investorId: number;
  investorName: string;
  email: string;
  emailStatus: string;
  notificationStatus: string;
  error: string | null;
};

type KavyaNote = {
  id: number;
  title: string;
  content: string;
  status: "active" | "archived";
  createdBy: string | null;
  updatedAt: string;
};

type RecipientPreview = {
  count: number;
  excludedPlaceholders: number;
  recipients: Array<{ investorId: number; name: string; email: string }>;
};

export default function Announcements({ cohortSlug }: { cohortSlug: string }) {
  const [tab, setTab] = useState<"announcements" | "kavya">("announcements");
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [notes, setNotes] = useState<KavyaNote[]>([]);
  const [recipients, setRecipients] = useState<RecipientPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [addToKavya, setAddToKavya] = useState(true);
  const [creating, setCreating] = useState(false);

  const [sendingId, setSendingId] = useState<number | null>(null);
  const [sendResult, setSendResult] = useState<{ id: number; text: string; ok: boolean } | null>(null);

  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [deliveries, setDeliveries] = useState<Record<number, Delivery[]>>({});

  const [noteTitle, setNoteTitle] = useState("");
  const [noteContent, setNoteContent] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [showRecipients, setShowRecipients] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [aRes, nRes, rRes] = await Promise.all([
        fetch(`${BASE}api/admin/cohorts/${cohortSlug}/announcements`, { headers: adminHeaders() }),
        fetch(`${BASE}api/admin/cohorts/${cohortSlug}/kavya-notes`, { headers: adminHeaders() }),
        fetch(`${BASE}api/admin/cohorts/${cohortSlug}/announcement-recipients`, { headers: adminHeaders() }),
      ]);
      if (!aRes.ok) throw new Error((await aRes.json()).error || `Failed to load announcements (${aRes.status})`);
      setAnnouncements(await aRes.json());
      setNotes(nRes.ok ? await nRes.json() : []);
      setRecipients(rRes.ok ? await rRes.json() : null);
    } catch (e: any) {
      setError(e?.message || "Failed to load");
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cohortSlug]);

  const createAnnouncement = async () => {
    setCreating(true);
    setError(null);
    try {
      const r = await fetch(`${BASE}api/admin/cohorts/${cohortSlug}/announcements`, {
        method: "POST",
        headers: adminHeaders(true),
        body: JSON.stringify({ subject: subject.trim(), body: body.trim(), addToKavya }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(Array.isArray(data.details) ? data.details.join("; ") : (data.error || "Create failed"));
      setSubject("");
      setBody("");
      await load();
    } catch (e: any) { setError(e.message); }
    setCreating(false);
  };

  const sendAnnouncement = async (a: Announcement) => {
    const n = recipients?.count ?? 0;
    const verb = a.status === "partial" ? "Retry sending" : "Send";
    if (!confirm(`${verb} "${a.subject}" to ${n} active investor${n === 1 ? "" : "s"} of this cohort?\n\nThis emails every recipient AND posts to their portal inbox. The text goes out exactly as written.`)) return;
    setSendingId(a.id);
    setSendResult(null);
    setError(null);
    try {
      const r = await fetch(`${BASE}api/admin/announcements/${a.id}/send`, {
        method: "POST",
        headers: adminHeaders(),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || `Send failed (${r.status})`);
      if (data.status === "already_sent") {
        setSendResult({ id: a.id, text: "Already fully sent — nothing was re-sent.", ok: true });
      } else if (data.failed > 0) {
        setSendResult({
          id: a.id,
          ok: false,
          text: `Partial: ${data.emailed} emailed, ${data.notified} inboxed, ${data.failed} FAILED — ${data.failures.map((f: any) => `${f.email}: ${f.error}`).join("; ")}. Use Retry to resume failed legs only.`,
        });
      } else {
        setSendResult({ id: a.id, ok: true, text: `Delivered to all ${data.total} investors (email + portal inbox).` });
      }
      await load();
    } catch (e: any) {
      setSendResult({ id: a.id, ok: false, text: e.message });
    }
    setSendingId(null);
  };

  const toggleDetail = async (id: number) => {
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    if (!deliveries[id]) {
      const r = await fetch(`${BASE}api/admin/announcements/${id}`, { headers: adminHeaders() });
      if (r.ok) {
        const data = await r.json();
        setDeliveries(prev => ({ ...prev, [id]: data.deliveries }));
      }
    }
  };

  const createNote = async () => {
    setSavingNote(true);
    setError(null);
    try {
      const r = await fetch(`${BASE}api/admin/cohorts/${cohortSlug}/kavya-notes`, {
        method: "POST",
        headers: adminHeaders(true),
        body: JSON.stringify({ title: noteTitle.trim(), content: noteContent.trim() }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(Array.isArray(data.details) ? data.details.join("; ") : (data.error || "Save failed"));
      setNoteTitle("");
      setNoteContent("");
      await load();
    } catch (e: any) { setError(e.message); }
    setSavingNote(false);
  };

  const toggleNoteStatus = async (note: KavyaNote) => {
    const next = note.status === "active" ? "archived" : "active";
    if (next === "archived" && !confirm(`Archive "${note.title}"? Kavya will stop using it immediately.`)) return;
    try {
      const r = await fetch(`${BASE}api/admin/kavya-notes/${note.id}`, {
        method: "PATCH",
        headers: adminHeaders(true),
        body: JSON.stringify({ status: next }),
      });
      if (!r.ok) throw new Error((await r.json()).error || "Update failed");
      await load();
    } catch (e: any) { setError(e.message); }
  };

  if (loading) return <div className="p-8 text-sm text-muted-foreground">Loading announcements…</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><Megaphone className="w-6 h-6 text-primary" /> Cohort Announcements</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Official notices sent verbatim to every active investor — by email and portal inbox — plus briefing notes Kavya answers from.
        </p>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" /> {error}
        </div>
      )}

      <div className="flex gap-1 border-b border-border">
        <button
          onClick={() => setTab("announcements")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === "announcements" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
        >
          Announcements
        </button>
        <button
          onClick={() => setTab("kavya")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === "kavya" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
        >
          Kavya Briefings {notes.filter(n => n.status === "active").length > 0 && `(${notes.filter(n => n.status === "active").length})`}
        </button>
      </div>

      {tab === "announcements" && (
        <div className="space-y-6">
          <div className="rounded-lg border border-border bg-card p-5 space-y-4">
            <h2 className="text-sm font-semibold flex items-center gap-2"><Plus className="w-4 h-4 text-primary" /> Compose announcement</h2>
            <p className="text-xs text-muted-foreground">
              The text below is sent <span className="font-semibold text-foreground">exactly as written</span> — no AI rewriting. Check every name and legal term before sending.
            </p>
            <input
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder="Subject (e.g. Important update: equity transfer notice)"
              maxLength={200}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              placeholder="Full announcement text. Blank lines create paragraphs in the email."
              rows={10}
              maxLength={20000}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={addToKavya} onChange={e => setAddToKavya(e.target.checked)} className="accent-primary" />
              Also add as a Kavya briefing note (Kavya answers investor questions from this exact text)
            </label>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <button
                onClick={() => setShowRecipients(s => !s)}
                className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
              >
                <Users className="w-3.5 h-3.5" />
                {recipients ? `${recipients.count} active investor${recipients.count === 1 ? "" : "s"} will receive this` : "Recipient preview unavailable"}
                {recipients && recipients.excludedPlaceholders > 0 && ` · ${recipients.excludedPlaceholders} placeholder account${recipients.excludedPlaceholders === 1 ? "" : "s"} excluded`}
                {showRecipients ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>
              <button
                onClick={createAnnouncement}
                disabled={creating || subject.trim().length < 3 || body.trim().length < 20}
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
              >
                Save draft
              </button>
            </div>
            {showRecipients && recipients && (
              <div className="rounded-md border border-border bg-muted/40 p-3 max-h-48 overflow-y-auto">
                {recipients.recipients.length === 0
                  ? <p className="text-xs text-muted-foreground">No active investors with real email addresses in this cohort.</p>
                  : <ul className="text-xs space-y-1">
                      {recipients.recipients.map(r => (
                        <li key={r.investorId} className="flex justify-between gap-4">
                          <span>{r.name}</span>
                          <span className="text-muted-foreground">{r.email}</span>
                        </li>
                      ))}
                    </ul>}
              </div>
            )}
            <ProgressLoader
              active={creating}
              title="Saving draft"
              stages={["Validating announcement text", "Saving draft", "Refreshing list"]}
            />
          </div>

          <div className="space-y-3">
            {announcements.length === 0 && (
              <p className="text-sm text-muted-foreground">No announcements yet for this cohort.</p>
            )}
            {announcements.map(a => (
              <div key={a.id} className="rounded-lg border border-border bg-card">
                <div className="p-4 space-y-2">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <h3 className="font-semibold text-sm">{a.subject}</h3>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {a.status === "sent" && a.sentAt && <>Sent {new Date(a.sentAt).toLocaleString()} · {a.recipientCount} recipients</>}
                        {a.status === "partial" && <span className="text-destructive font-medium">Partially sent — {a.failedCount} recipient{a.failedCount === 1 ? "" : "s"} failed. Retry resumes only the failed legs.</span>}
                        {a.status === "draft" && <>Draft · created {new Date(a.createdAt).toLocaleString()}{a.createdBy ? ` by ${a.createdBy}` : ""}</>}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {a.status === "sent" && <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2.5 py-1 text-xs font-medium"><CheckCircle2 className="w-3.5 h-3.5" /> Sent</span>}
                      {(a.status === "draft" || a.status === "partial") && (
                        <button
                          onClick={() => sendAnnouncement(a)}
                          disabled={sendingId !== null}
                          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
                        >
                          <Send className="w-3.5 h-3.5" /> {a.status === "partial" ? "Retry failed" : "Send to cohort"}
                        </button>
                      )}
                      {(a.recipientCount ?? 0) > 0 && (
                        <button onClick={() => toggleDetail(a.id)} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
                          Deliveries {expandedId === a.id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground whitespace-pre-wrap line-clamp-4">{a.body}</p>
                  {sendResult?.id === a.id && (
                    <p className={`text-xs font-medium ${sendResult.ok ? "text-primary" : "text-destructive"}`}>{sendResult.text}</p>
                  )}
                  <ProgressLoader
                    active={sendingId === a.id}
                    title={`Sending to ${recipients?.count ?? "all"} investors`}
                    stages={[
                      "Locking announcement for send",
                      "Building recipient list",
                      "Posting to portal inboxes",
                      "Sending emails one by one",
                      "Recording per-recipient delivery status",
                    ]}
                    stageIntervalMs={3000}
                  />
                </div>
                {expandedId === a.id && deliveries[a.id] && (
                  <div className="border-t border-border p-4 max-h-64 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-left text-muted-foreground">
                          <th className="pb-2 font-medium">Investor</th>
                          <th className="pb-2 font-medium">Email</th>
                          <th className="pb-2 font-medium">Inbox</th>
                          <th className="pb-2 font-medium">Email sent</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deliveries[a.id].map(d => (
                          <tr key={d.investorId} className="border-t border-border/50">
                            <td className="py-1.5">{d.investorName}</td>
                            <td className="py-1.5 text-muted-foreground">{d.email}</td>
                            <td className="py-1.5">{d.notificationStatus === "delivered" ? <CheckCircle2 className="w-3.5 h-3.5 text-primary" /> : <XCircle className="w-3.5 h-3.5 text-destructive" />}</td>
                            <td className="py-1.5">
                              {d.emailStatus === "delivered"
                                ? <CheckCircle2 className="w-3.5 h-3.5 text-primary" />
                                : <span className="inline-flex items-center gap-1 text-destructive"><XCircle className="w-3.5 h-3.5" />{d.error && <span className="truncate max-w-[220px]" title={d.error}>{d.error}</span>}</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "kavya" && (
        <div className="space-y-6">
          <div className="rounded-lg border border-border bg-card p-5 space-y-4">
            <h2 className="text-sm font-semibold flex items-center gap-2"><BookOpenText className="w-4 h-4 text-primary" /> New briefing note</h2>
            <p className="text-xs text-muted-foreground">
              Kavya treats these as the official team position and quotes them verbatim to investors of this cohort.
              For follow-up questions the note doesn't answer, she says she doesn't have that detail and offers to flag it to the team — she never guesses.
            </p>
            <input
              value={noteTitle}
              onChange={e => setNoteTitle(e.target.value)}
              placeholder="Topic title (e.g. Equity transfer to Tradity Advisors LLP)"
              maxLength={200}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <textarea
              value={noteContent}
              onChange={e => setNoteContent(e.target.value)}
              placeholder="The exact position Kavya should communicate. Write it the way you'd want an investor to read it."
              rows={8}
              maxLength={20000}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <div className="flex justify-end">
              <button
                onClick={createNote}
                disabled={savingNote || noteTitle.trim().length < 3 || noteContent.trim().length < 20}
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
              >
                Add briefing
              </button>
            </div>
            <ProgressLoader
              active={savingNote}
              title="Saving briefing note"
              stages={["Validating note", "Saving to Kavya's knowledge", "Refreshing list"]}
            />
          </div>

          <div className="space-y-3">
            {notes.length === 0 && <p className="text-sm text-muted-foreground">No briefing notes yet. Kavya only knows what you put here (plus the standard portfolio data).</p>}
            {notes.map(n => (
              <div key={n.id} className={`rounded-lg border border-border bg-card p-4 space-y-2 ${n.status === "archived" ? "opacity-60" : ""}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-sm">{n.title}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {n.status === "archived" ? "Archived — Kavya no longer uses this" : `Active · updated ${new Date(n.updatedAt).toLocaleString()}`}
                      {n.createdBy ? ` · ${n.createdBy}` : ""}
                    </p>
                  </div>
                  <button
                    onClick={() => toggleNoteStatus(n)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted flex-shrink-0"
                  >
                    <Archive className="w-3.5 h-3.5" /> {n.status === "active" ? "Archive" : "Reactivate"}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground whitespace-pre-wrap">{n.content}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
